import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react'
import { mapValueKey } from '@editorUtils'
import {
  Scene,
  ModelMesh,
  DomainMesh,
  elementTypes,
  Snowfall,
  SceneMarker,
  ClipController,
  ModelElementLabelsV2,
  ModelMeshV2,
  CommentsController,
  IFCGlbModel,
  SceneLoadingIndicator,
  PlanAreaFrame,
  StoreyImages,
  SceneElementDebugController,
  SceneElementDebugBox,
} from '@scene'
import { last } from 'lodash-es'
import { Perf } from 'r3f-perf'
import { Euler, AxesHelper, Sprite, SpriteMaterial, CanvasTexture } from 'three'
import { Box, Stack } from '@mui/material'
import { ADD_PLAN_IMAGE_CARDS_SELECTION_KEY } from '@editor/hooks/usePlanImageCardTool'
import { ADD_PLAN_TABLES_SELECTION_KEY } from '@editor/hooks/usePlanTableTool'
import { FloorPlan } from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/components'
import { useCameraStore } from '@modugen/ui-toolkit'
import { NavbarPortalLeft, NavbarPortalRight } from '@ui/navigation'
import { Context, CoordinateExplorerScene } from '@ui/scene'
import { useAppStore } from '@stores'
import {
  useControlStore,
  useModelStore,
  useElementSelectionStore,
  createInteractableByType,
  useTapelineStore,
} from '@editorStores'
import {
  useModelClickListeners,
  useModelHoverListeners,
  useFloorplanViewActive,
  useHiddenElements,
  useCameraEvent,
  useSelectionMode,
  useElementCrossSectionAssignment,
} from '@editorHooks'
import AIChatFindingMarkers from 'src/components/scene/AIChatFindingMarkers'
import PlanImageCardController from 'src/components/scene/PlanImageCardController'
import PlanLabelController from 'src/components/scene/PlanLabelController'
import PlanTableController from 'src/components/scene/PlanTableController'
import { appConfig } from 'src/constants'
import { ControlsLeft, ControlsRight } from '../Controls'
import FloorplanScene from '../FloorplanScene'
import MarqueeSelectionController from '../MarqueeSelectionController'

interface Props {
  children?: ReactNode
  addOns?: ReactNode
  /** The scene is covered by an overlay: stop rendering frames while it is (see Scene's `paused`). */
  isCovered?: boolean
}

const SceneControlled = ({ children, addOns, isCovered = false }: Props): ReactElement => {
  const gridEnabled = useControlStore(state => state.gridEnabled)
  const axesEnabled = useControlStore(state => state.axesEnabled)
  const coordinateExplorerEnabled = useControlStore(state => state.coordinateExplorerEnabled)
  const isSceneBlocked = useControlStore(state => state.isSceneBlocked)

  const showSnow = useAppStore(state => state.showSnowV2)
  const showStats = useAppStore(state => state.showStats)
  const sceneElementDebug = useAppStore(state => state.sceneElementDebug)
  const batchedModelRenderingV2 = useAppStore(state => state.batchedModelRenderingV2)

  const model = useModelStore(state => state.model)

  const domains = useModelStore(state => state.domains)
  const visibleDomains = useModelStore(state => state.visibleDomains)
  const hiddenElements = useHiddenElements()
  const interactableByTypeConfigs = useModelStore(state => state.interactableByTypeConfigs)

  const blockingElements = useModelStore(state => state.blockingElements)
  const translucent = useModelStore(state => state.translucent)
  const floorplanViewActive = useFloorplanViewActive()
  const showComments = useModelStore(state => state.showComments)
  const isTapelineActive = useTapelineStore(state => state.isActive)
  const { selectionModeKey } = useSelectionMode()
  const isPlanTablePlacementMode = selectionModeKey === ADD_PLAN_TABLES_SELECTION_KEY
  const isPlanImageCardPlacementMode = selectionModeKey === ADD_PLAN_IMAGE_CARDS_SELECTION_KEY
  const highlightedSecondaryIds = useElementSelectionStore(state => state.highlightedSecondaryIds)
  const selectedStandAloneIds = useElementSelectionStore(state => state.selectedStandAloneIds)
  const highlightedIds = useElementSelectionStore(state => state.highlightedIds)
  const selectionEnabled = useElementSelectionStore(state => state.selectionEnabled)

  const onModelClick = useModelClickListeners()
  const [onPointerOver, onPointerLeave] = useModelHoverListeners()

  // MEMOS

  const interactableByType = useMemo(
    () =>
      last(interactableByTypeConfigs)?.interactableByType || createInteractableByType(elementTypes),
    [interactableByTypeConfigs],
  )

  const elementCrossSectionAssignment = useElementCrossSectionAssignment()

  const guidToCrossSection = useMemo(() => {
    const elements = elementCrossSectionAssignment.map(assignment => ({
      ...assignment,
      shape: assignment.element_cs.shape,
    }))

    return {
      ...mapValueKey(elements, 'element_guid'),
    }
  }, [elementCrossSectionAssignment])

  const isOrthographic = useCameraStore(state => state.isOrthographic)

  const modelBox = useModelStore(state => state.initialModelBoundingBox)
  const modelCenter = useModelStore(state => state.initialModelCenter)

  const [initialModelBoundaries, setInitialModelBoundaries] = useState<{
    modelCenter: [number, number, number]
    modelBox: {
      min: [number, number, number]
      max: [number, number, number]
    }
  } | null>(null)

  useEffect(() => {
    if (modelBox && modelCenter && !initialModelBoundaries) {
      setInitialModelBoundaries({
        modelCenter,
        modelBox,
      })
    }
  }, [modelBox, modelCenter, initialModelBoundaries])

  const cameraEvent = useMemo(() => {
    if (floorplanViewActive && isOrthographic && initialModelBoundaries) {
      const padding = 1.6
      const event: FitToBoxOrthographicCameraEvent = {
        cameraType: 'orthographic',
        type: 'fitToBoxOrthographic',
        box: initialModelBoundaries.modelBox,
        options: {
          paddingLeft: padding,
          paddingRight: padding,
          paddingTop: padding,
          paddingBottom: padding,
        },
        priority: 1,
      }

      return event
    } else if (initialModelBoundaries) {
      const event: PositionPerspectiveCameraEvent = {
        cameraType: 'perspective',
        type: 'positionPerspective',
        position: initialModelBoundaries.modelCenter,
        priority: 0,
      }
      return event
    }
    return null
  }, [floorplanViewActive, isOrthographic, initialModelBoundaries])

  useCameraEvent(cameraEvent)

  const createTextSprite = (text: string, color: string) => {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    canvas.width = 256
    canvas.height = 256

    if (context) {
      context.font = 'Bold 60px Arial'
      context.fillStyle = color
      context.textAlign = 'center'
      context.fillText(text, 128, 128)
    }

    const texture = new CanvasTexture(canvas)
    const spriteMaterial = new SpriteMaterial({ map: texture })
    const sprite = new Sprite(spriteMaterial)
    sprite.scale.set(2, 2, 2)

    return sprite
  }

  const AxesWithLabels = () => {
    const axesHelper = new AxesHelper(10)

    const xLabel = createTextSprite('X', '#ff0000')
    const yLabel = createTextSprite('Y', '#000000')
    const zLabel = createTextSprite('Z', '#0000ff')

    xLabel.position.set(3, -1, 0)
    yLabel.position.set(-1, 3, 0)
    zLabel.position.set(0, 0, 10)

    axesHelper.add(xLabel)
    axesHelper.add(yLabel)
    axesHelper.add(zLabel)

    return <primitive object={axesHelper} />
  }

  const floorplanStateStorey = useControlStore(state => state.floorplanState.storey)

  const setIsOrthographic = useCameraStore(state => state.setIsOrthographic)

  // The camera follows the floorplan view. `invisible` needs no effect at all — it is derived
  // wherever it is read (`useFloorplanViewActive`), so nothing can push it out of sync.
  useEffect(() => {
    setIsOrthographic(floorplanViewActive)

    // Ensure everything is reset when leaving results as we
    // do not have same behaviour globally yet
    return () => setIsOrthographic(false)
  }, [floorplanViewActive, setIsOrthographic])

  return (
    <Box
      position="relative"
      flexGrow={1}
      alignSelf="stretch"
      overflow="hidden"
      height="100%"
      // Establish a query container so right-anchored scene overlays (e.g. StoreyControls) can
      // cap their width to this pane's width via `cqw` units instead of the full viewport (`vw`).
      sx={{ containerType: 'inline-size' }}
    >
      {isSceneBlocked && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          sx={{
            background: 'black',
            opacity: 0.1,
            zIndex: 1,
          }}
          data-cy="scene-blocker"
        />
      )}
      {addOns}
      <Scene paused={isCovered}>
        {gridEnabled && <gridHelper args={[200, 400]} rotation={new Euler(1.5708)} />}
        {axesEnabled && <AxesWithLabels />}
        {coordinateExplorerEnabled && <CoordinateExplorerScene />}
        <SceneMarker />
        <PlanAreaFrame />
        {showComments && <CommentsController />}
        <AIChatFindingMarkers />
        {!isOrthographic && <ClipController />}

        {!floorplanViewActive && (
          <group visible={!floorplanViewActive}>
            <ModelMesh
              model={model}
              onClick={onModelClick}
              translucent={translucent}
              interactableByType={interactableByType}
              blockingElements={blockingElements}
              hiddenElements={hiddenElements}
              noPointerInteractions={
                isTapelineActive || isPlanTablePlacementMode || isPlanImageCardPlacementMode
              }
              selectedElementIds={selectedStandAloneIds}
              highlightedIds={highlightedIds}
              highlightedSecondaryIds={highlightedSecondaryIds}
              onPointerOver={onPointerOver}
              onPointerLeave={onPointerLeave}
              applyThickness={false}
              guidToCrossSection={guidToCrossSection}
              skipBatchedShapes={batchedModelRenderingV2}
            />
            <ModelMeshV2 />
            <DomainMesh
              domains={domains}
              domainGuids={visibleDomains}
              hiddenElements={hiddenElements}
            />
          </group>
        )}

        <ModelElementLabelsV2 />

        <IFCGlbModel />

        <StoreyImages />

        {/* Unmount in 3D: drei Html labels ignore parent `visible={false}` and would
            otherwise keep overlaying the perspective scene. */}
        {floorplanStateStorey !== null && (
          <FloorPlan key={`floorplan`} activeStorey={floorplanStateStorey} />
        )}

        {/* For now we need to disable this in the structural
           planning view, because the floorplan is rendered 
           in the structural planning scene already.
           Also we need to completely unmount the floorplan scene when the 
           floorplan is not shown, as the component still blocks some mouse 
           interactions due to lines that are not visible
           */}
        {floorplanStateStorey !== null && isOrthographic && <FloorplanScene />}

        <PlanTableController />
        <PlanImageCardController />
        <PlanLabelController />

        {appConfig.enableSnowEasterEgg && showSnow && <Snowfall />}

        {showStats && <Perf position="top-left" />}

        {sceneElementDebug && <SceneElementDebugController />}

        <Context />

        <MarqueeSelectionController />

        <SceneLoadingIndicator />

        {children}
      </Scene>
      {sceneElementDebug && <SceneElementDebugBox />}
      <NavbarPortalLeft>
        <Stack direction="row" spacing={1}>
          <ControlsLeft selectionControls={selectionEnabled} />
        </Stack>
      </NavbarPortalLeft>
      <NavbarPortalRight>
        <ControlsRight />
      </NavbarPortalRight>
    </Box>
  )
}

export default SceneControlled
